import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { changePassword } from "../../api/admin";
import {
  TextField,
  Box,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "./Styling.css";
import { s3BaseUrl } from "../../config/envConfig";

export default function ChangePassword() {
  const history = useHistory();
  const { id, token } = useParams();

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [formErrors, setFormErrors] = useState({
    password: false,
    confirmPassword: false,
  });
  const [passwordCriteriaMessage, setPasswordCriteriaMessage] = useState(false);
  const [passwordMatchMessage, setPasswordMatchMessage] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Password validation criteria
  const passwordCriteria =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&#^.,;:'"<>{}[\]()/|`~+=_-]{8,}$/;

  const handleInputChange = (key, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const validateField = (key, value) => {
    let isValid = value.length !== 0;

    if (key === "password") {
      isValid = isValid && passwordCriteria.test(value);
      setPasswordCriteriaMessage(!passwordCriteria.test(value) && value !== "");
    }

    if (key === "confirmPassword") {
      isValid = isValid && value === formData.password;
      setPasswordMatchMessage(value !== formData.password && value !== "");
    }

    return isValid;
  };

  const handleBlur = (key, value) => {
    const isValid = validateField(key, value);
    setFormErrors((prevState) => ({
      ...prevState,
      [key]: !isValid,
    }));
  };

  const handleSubmit = async () => {
    const newFormErrors = {
      password: !validateField("password", formData.password),
      confirmPassword: !validateField(
        "confirmPassword",
        formData.confirmPassword
      ),
    };

    setFormErrors(newFormErrors);
    const isValid = Object.values(newFormErrors).every((error) => !error);

    if (isValid) {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const updatePasswordResponse = await changePassword(
          id,
          token,
          formData.password
        );
        setIsLoading(false);
        if (updatePasswordResponse.code === 200) {
          history.push("/login");
        } else {
          setErrorMessage("Unable to change password. Please try again.");
        }
      } catch (error) {
        setIsLoading(false);
        setErrorMessage("Error updating password.");
      }
    } else {
      setFormValid(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: windowWidth >= 800 ? "50%" : "90%",
      }}
    >
      <div>
        <img
          src={
            s3BaseUrl + "/mobitutorza_logo_full.png"
          }
          alt={"MobiTutorZA Logo"}
          width={"250px"}
          height={"250px"}
        />
      </div>
      <div>
        <p style={{ color: "white", fontSize: "1.25em", fontWeight: "bold" }}>
          Update Password
        </p>
        <hr style={{ color: "white", width: "100%", marginBottom: "20px" }} />
      </div>

      {isLoading ? (
        <CircularProgress color="inherit" />
      ) : (
        <>
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
            noValidate
            autoComplete="off"
          >
            <div>
              <p style={{ color: "#FBCD02" }}>
                Password must be at least 8 characters long and contain a mix of
                uppercase letters, lowercase letters, and numbers.
              </p>
            </div>
            <TextField
              sx={{
                backgroundColor: "rgba(224, 224, 224, 0.12)",
                borderRadius: "5px",
                "& .MuiInputBase-input": {
                  padding: "15px",
                  fontSize: "0.875rem",
                  color: "white",
                },
                "&.Mui-focused": {
                  borderColor: "#00112e",
                  "--joy-palette-primary-500": "#01ACC7",
                },
                width: "100%",
              }}
              InputProps={{
                sx: {
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#FBCD02",
                  },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      sx={{ color: "white" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: {
                  color: "#fbcd02",
                  "&.Mui-focused": {
                    color: "#fbcd02",
                  },
                },
              }}
              error={formErrors.password}
              helperText={formErrors.password ? "Password is required." : ""}
              label="Password"
              variant="outlined"
              type={showPassword ? "text" : "password"}
              onChange={(e) => handleInputChange("password", e.target.value)}
              onBlur={(e) => handleBlur("password", e.target.value)}
            />

            <TextField
              sx={{
                backgroundColor: "rgba(224, 224, 224, 0.12)",
                borderRadius: "5px",
                "& .MuiInputBase-input": {
                  padding: "15px",
                  fontSize: "0.875rem",
                  color: "white",
                },
                "&.Mui-focused": {
                  borderColor: "#00112e",
                  "--joy-palette-primary-500": "#01ACC7",
                },
                width: "100%",
              }}
              InputProps={{
                sx: {
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#FBCD02",
                  },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleToggleConfirmPasswordVisibility}
                      sx={{ color: "white" }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: {
                  color: "#fbcd02",
                  "&.Mui-focused": {
                    color: "#fbcd02",
                  },
                },
              }}
              error={formErrors.confirmPassword}
              helperText={
                formErrors.confirmPassword ? "Passwords must match." : ""
              }
              label="Confirm Password"
              variant="outlined"
              type={showConfirmPassword ? "text" : "password"}
              onChange={(e) =>
                handleInputChange("confirmPassword", e.target.value)
              }
              onBlur={(e) => handleBlur("confirmPassword", e.target.value)}
            />

            {/* {passwordMatchMessage && (
              <p style={{ color: '#fbcd02' }}>Passwords do not match.</p>
            )} */}

            {errorMessage && (
              <div style={{ color: "red", marginBottom: "20px" }}>
                {errorMessage}
              </div>
            )}

            <Button
              variant="contained"
              style={{
                backgroundColor: "#FBCD02",
                color: "#00112e",
                borderRadius: 5,
                marginTop: "20px",
              }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Box>
        </>
      )}
    </div>
  );
}
